import type { PlaybookCondition } from "@repo/app-types/entities/filterConditions";
import {
  flattenPlaybookTreeWithDepth,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { MenuSeparator } from "@repo/ui/Menu/menuSeparator";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { type FC, useMemo } from "react";
import { Collection, SubmenuTrigger } from "react-aria-components";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { PlaybookConditionMenu } from "./playbookConditionMenu.js";

export interface PlaybookConditionsMenuProps {
  playbookId: PlaybookId;
  includeCallsWithoutGrades: boolean;
  onIncludeCallsWithoutGradesChange: (include: boolean) => void;
  onSelect: (condition: PlaybookCondition) => void;
}

export const PlaybookConditionsMenu: FC<PlaybookConditionsMenuProps> = (
  props,
) => {
  const { playbookId } = props;
  const { data: playbook } = usePlaybookByIdQuery(playbookId);

  // react-aria's Collection keys items by `id`, which the shared flattened
  // entry doesn't carry.
  const nodes = useMemo(
    () =>
      playbook
        ? flattenPlaybookTreeWithDepth(playbook.assessment_tree).map(
            (entry) => ({ id: entry.node.id, ...entry }),
          )
        : undefined,
    [playbook],
  );

  // TODO: Show loading state
  return (
    <>
      <div className="px-2 py-1.5">
        <Checkbox
          className="cursor-default"
          isChecked={props.includeCallsWithoutGrades}
          onChange={props.onIncludeCallsWithoutGradesChange}
          label="Include calls without grades"
        />
      </div>
      <MenuSeparator />
      <SimpleMenu aria-label="Filter by playbook result" className="max-w-xs">
        <Collection items={nodes}>
          {({ node, depth }) => (
            <SubmenuTrigger>
              <SimpleMenuItem indent={depth}>{node.name}</SimpleMenuItem>

              <SimpleMenuPopover>
                <PlaybookConditionMenu
                  playbookId={playbookId}
                  node={node}
                  onSelect={props.onSelect}
                />
              </SimpleMenuPopover>
            </SubmenuTrigger>
          )}
        </Collection>
      </SimpleMenu>
    </>
  );
};
